import { upstreamField } from '../domain/errors';
import {
  type MembershipFormIncident,
  type MembershipPurchaseError,
  type MembershipPurchaseFailure,
  type MembershipPurchaseIncident,
  summarizeFormError,
} from '../domain/purchase';
import type { Membership } from '../domain/types';
import { CardFormLoadError } from '../payment/CardForm';

/** What Join does about a failed purchase or confirmation request. */
export interface PurchaseErrorOutcome {
  /** Sentry incident; `null` for failures that are not reported (401, 409). */
  incident: MembershipPurchaseIncident | null;
  /** `reconcile` asks `/me` before settling on `failure`; nothing is ever re-sent. */
  next: 'fail' | 'reconcile';
  failure: MembershipPurchaseFailure;
  /** Data the failure proved stale. */
  refresh: 'membership' | 'plans' | null;
}

/**
 * Classifies a failed `POST /memberships`: an unknown outcome is checked
 * through `/me` before a new attempt is offered, a payment the service
 * refused (nothing charged) fails at once with a new form.
 */
export const purchaseErrorOutcome = (
  error: MembershipPurchaseError,
): PurchaseErrorOutcome => {
  const { kind, status, code, upstream } = error;
  switch (kind) {
    case 'unknown_outcome':
      return {
        incident: {
          reason: 'purchaseFailed',
          kind,
          status,
          code,
          ...upstreamField(upstream),
          level: 'error',
        },
        next: 'reconcile',
        failure: 'payment_unavailable',
        refresh: null,
      };
    case 'payment_rejected':
      return {
        incident: {
          reason: 'purchaseFailed',
          kind,
          status,
          code,
          ...upstreamField(upstream),
          level: 'error',
        },
        next: 'fail',
        failure: 'payment_rejected',
        refresh: null,
      };
    case 'card_refused':
      return {
        incident: { reason: 'cardRefused', status, code },
        next: 'fail',
        failure: 'card_refused',
        refresh: null,
      };
    case 'already_exists':
      return {
        incident: null,
        next: 'fail',
        failure: 'already_member',
        refresh: 'membership',
      };
    case 'plan_not_found':
      return {
        incident: {
          reason: 'purchaseFailed',
          kind,
          status,
          code,
          ...upstreamField(upstream),
          level: 'warning',
        },
        next: 'fail',
        failure: 'plan_not_found',
        refresh: 'plans',
      };
    case 'unauthorized':
      return {
        incident: null,
        next: 'fail',
        failure: 'payment_unavailable',
        refresh: null,
      };
    default:
      return {
        incident: {
          reason: 'purchaseFailed',
          kind,
          status,
          code,
          ...upstreamField(upstream),
          level: 'error',
        },
        next: 'fail',
        failure: 'payment_unavailable',
        refresh: null,
      };
  }
};

/**
 * Classifies a failed `confirm-card`: a refusal and a payment the service
 * refused (nothing charged) fail at once, anything else is checked through
 * `/me`. An unknown outcome settles like a lost purchase answer, so a
 * `pending_card` that may still be charging leaves for "Activating…".
 * A 401 and a 409 are not reported.
 */
export const confirmErrorOutcome = (
  error: MembershipPurchaseError,
): PurchaseErrorOutcome => {
  const { kind, status, code, upstream } = error;
  if (kind === 'card_refused') {
    return {
      incident: { reason: 'cardRefused', status, code },
      next: 'fail',
      failure: 'card_refused',
      refresh: null,
    };
  }
  if (kind === 'payment_rejected') {
    return {
      incident: {
        reason: 'confirmFailed',
        kind,
        status,
        code,
        ...upstreamField(upstream),
      },
      next: 'fail',
      failure: 'payment_rejected',
      refresh: null,
    };
  }
  const isQuiet = kind === 'unauthorized' || kind === 'already_exists';
  return {
    incident: isQuiet
      ? null
      : {
          reason: 'confirmFailed',
          kind,
          status,
          code,
          ...upstreamField(upstream),
        },
    next: 'reconcile',
    failure:
      kind === 'unknown_outcome' ? 'payment_unavailable' : 'not_confirmed',
    refresh: null,
  };
};

/** How `/me` settles a purchase whose outcome was unknown. */
export type ReconcileOutcome =
  | { kind: 'member'; status: 'trialing' | 'active'; membership: Membership }
  | { kind: 'activating' }
  /** `/me` itself failed: the outcome is still unknown, so no new payment is offered. */
  | { kind: 'unknown' }
  | { kind: 'failed' };

/**
 * A membership means the purchase went through. After a lost purchase or
 * confirmation answer (`otherwise` is `payment_unavailable`) `pending_card`
 * means the charge may still be running, so Join leaves for "Activating…"
 * instead of offering a second payment. `undefined` is a `/me` that failed:
 * it says nothing about the purchase, unlike `null` (no membership, a 404),
 * so it never settles on `otherwise` and its fresh card form.
 */
export const reconcileOutcome = (
  membership: Membership | null | undefined,
  otherwise: MembershipPurchaseFailure,
): ReconcileOutcome => {
  if (membership === undefined) {
    return { kind: 'unknown' };
  }
  const status = membership?.status;
  if (membership && (status === 'trialing' || status === 'active')) {
    return { kind: 'member', status, membership };
  }
  if (status === 'pending_card' && otherwise === 'payment_unavailable') {
    return { kind: 'activating' };
  }
  return { kind: 'failed' };
};

/**
 * Incident of a card form that could not start: a lost chunk is reported as
 * such, an SDK failure by its name and message only.
 */
export const formStartIncident = (error: unknown): MembershipFormIncident =>
  error instanceof CardFormLoadError
    ? { reason: 'cardFormChunkFailed', error: error.reason }
    : {
        reason: 'adyenInitFailed',
        stage: 'init',
        error: summarizeFormError(error),
      };

/** Incident of a card form that failed once mounted, during a 3DS challenge too; never carries the SDK error itself. */
export const formRuntimeIncident = (
  error: unknown,
): MembershipFormIncident => ({
  reason: 'adyenInitFailed',
  stage: 'runtime',
  error: summarizeFormError(error),
});
